/**
 * Kiosk-state diagnostic helpers.
 *
 * Reads the `device_meta.kiosk_state` block emitted by the Vortex Launcher
 * during Updates → Check All and computes operator-facing health labels +
 * per-field interpretation.
 *
 * Supports two schemas:
 *  - v1 (Launcher 2.42.x, PR #365): flat 10-field snapshot. No schema_version
 *    field. Detected by absence of schema_version.
 *  - v2 (Launcher PR #368+): schema_version=2, grouped into 14 sections, plus
 *    v1 flat compatibility fields mirrored at the top level. `complete` flags
 *    mean read-completeness (read succeeded), NEVER health-correctness.
 *
 * Pure functions only — no side effects, no UI, no network.
 */

// ─── v1 types (preserved) ───────────────────────────────────────────────

export interface KioskStateV1 {
  uid?: number | null;
  is_platform_signed?: boolean | null;
  is_device_owner?: boolean | null;
  lock_task_mode_state?: number | null;
  back_gesture_inset_scale_right?: string | null;
  back_gesture_inset_scale_left?: string | null;
  navigation_mode?: string | null;
  one_handed_mode_enabled?: string | null;
  accessibility_display_magnification_enabled?: string | null;
  kiosk_state_complete?: boolean;
}

/**
 * Back-compat alias for callers that imported `KioskState` before v2 landed.
 * New code should prefer `KioskStateV1` or `KioskStateAny`.
 */
export type KioskState = KioskStateV1;

// ─── v2 section types ───────────────────────────────────────────────────

export interface KioskV2IdentityPrivilege {
  uid?: number | null;
  is_platform_signed?: boolean | null;
  is_device_owner?: boolean | null;
  android_sdk_int?: number | null;
  device_model?: string | null;
  complete: boolean;
}

export interface KioskV2LockTaskKiosk {
  lock_task_mode_state?: number | null;
  lock_task_features?: number | null;
  lock_task_packages_count?: number | null;
  lock_task_includes_vortexnav?: boolean | null;
  lock_task_includes_settings?: boolean | null;
  keyguard_locked_runtime?: boolean | null;
  complete: boolean;
}

export interface KioskV2UserRestrictions {
  disallow_safe_boot?: boolean | null;
  disallow_add_user?: boolean | null;
  disallow_config_credentials?: boolean | null;
  disallow_factory_reset?: boolean | null;
  disallow_config_brightness?: boolean | null;
  complete: boolean;
}

export interface KioskV2GestureHardening {
  back_gesture_inset_scale_right?: string | null;
  back_gesture_inset_scale_left?: string | null;
  camera_double_tap_power_gesture_disabled?: string | null;
  assist_gesture_enabled?: string | null;
  assist_gesture_silence_alerts_enabled?: string | null;
  assist_gesture_wake_enabled?: string | null;
  one_handed_mode_enabled?: string | null;
  one_handed_mode_activated?: string | null;
  notification_bubbles?: string | null;
  accessibility_display_magnification_enabled?: string | null;
  accessibility_display_magnification_navbar_enabled?: string | null;
  wake_gesture_enabled?: string | null;
  pickup_gesture?: string | null;
  tap_gesture?: string | null;
  doze_always_on?: string | null;
  doze_pulse_on_pick_up?: string | null;
  doze_pulse_on_double_tap?: string | null;
  complete: boolean;
}

export interface KioskV2Navigation {
  navigation_mode?: string | null;
  complete: boolean;
}

export interface KioskV2SystemUiHardening {
  policy_control?: string | null;
  heads_up_notifications_enabled?: string | null;
  hide_error_dialogs?: string | null;
  stay_on_while_plugged_in?: string | null;
  window_animation_scale?: string | null;
  transition_animation_scale?: string | null;
  animator_duration_scale?: string | null;
  force_resizable_activities?: string | null;
  complete: boolean;
}

export interface KioskV2LockscreenKeyguard {
  lockscreen_disabled?: string | null;
  lock_screen_show_notifications?: string | null;
  lock_screen_allow_private_notifications?: string | null;
  lock_screen_lock_after_timeout?: string | null;
  complete: boolean;
}

export interface KioskV2InputAccessibility {
  default_input_method?: string | null;
  enabled_input_methods_includes_heliboard?: boolean | null;
  accessibility_enabled?: string | null;
  back_key_interceptor_enabled?: boolean | null;
  immersive_mode_confirmations?: string | null;
  install_non_market_apps?: string | null;
  swipe_bottom_to_notification_enabled?: string | null;
  complete: boolean;
}

export interface KioskV2DisplayPower {
  screen_off_timeout?: string | null;
  screen_brightness_mode?: string | null;
  accelerometer_rotation?: string | null;
  user_rotation?: string | null;
  font_scale?: string | null;
  status_bar_show_battery_percent?: string | null;
  time_12_24?: string | null;
  haptic_feedback_enabled?: string | null;
  sound_effects_enabled?: string | null;
  double_tap_to_wake?: string | null;
  sleep_timeout?: string | null;
  density_dpi?: number | null;
  complete: boolean;
}

export interface KioskV2ConnectivityPolicy {
  data_roaming?: string | null;
  mobile_data?: string | null;
  wifi_scan_always_enabled?: string | null;
  ble_scan_always_enabled?: string | null;
  adb_enabled?: string | null;
  development_settings_enabled?: string | null;
  airplane_mode_on?: string | null;
  complete: boolean;
}

export interface KioskV2PackageEntry {
  installed?: boolean | null;
  hidden?: boolean | null;
}

export interface KioskV2PackagePolicy {
  complete: boolean;
  [pkg: string]: KioskV2PackageEntry | boolean | undefined;
}

export interface KioskV2AppPermissionEntry {
  installed?: boolean | null;
  location_requested?: boolean | null;
  location_granted?: boolean | null;
  media_requested?: boolean | null;
  media_granted?: boolean | null;
  bluetooth_requested?: boolean | null;
  bluetooth_granted?: boolean | null;
  complete?: boolean;
}

export interface KioskV2AppPermissions {
  vortexnav?: KioskV2AppPermissionEntry;
  truehelm?: KioskV2AppPermissionEntry;
  vortextide?: KioskV2AppPermissionEntry;
  vortexanchor?: KioskV2AppPermissionEntry;
  complete: boolean;
}

export interface KioskV2LauncherMeta {
  launcher_version_name?: string | null;
  launcher_version_code?: number | null;
  signing_fingerprint_short?: string | null;
  complete: boolean;
}

export interface KioskV2DisplayInputContext {
  display_width_px?: number | null;
  display_height_px?: number | null;
  density_dpi?: number | null;
  rotation?: number | null;
  orientation?: string | null;
  pointer_location_enabled?: string | null;
  complete: boolean;
}

/**
 * runtime_drift (Launcher PR — runtime-drift telemetry): LIVE window geometry
 * and system-gesture insets captured at update-check time, NOT stored Settings.
 * Exposes the recurring SystemUI EdgeBackGestureHandler desync that every
 * stored-value section misses (stored back_gesture_inset_scale stays "0" while
 * the gesture re-enables at runtime). On this firmware EdgeBackGestureHandler
 * is the InsetsFrameProvider, so a non-zero left/right system_gesture_inset
 * while the stored scale is "0" is the in-process fingerprint of the desync.
 * Optional — only present on launcher builds new enough to report it.
 */
export interface KioskV2RuntimeDrift {
  max_window_bounds_w?: number | null;
  max_window_bounds_h?: number | null;
  current_window_bounds_w?: number | null;
  current_window_bounds_h?: number | null;
  display_rotation?: number | null;
  config_orientation?: string | null;
  real_metrics_w?: number | null;
  real_metrics_h?: number | null;
  landscape_consistent?: boolean | null;
  insets_captured?: boolean | null;
  system_gesture_inset_left?: number | null;
  system_gesture_inset_top?: number | null;
  system_gesture_inset_right?: number | null;
  system_gesture_inset_bottom?: number | null;
  mandatory_gesture_inset_left?: number | null;
  mandatory_gesture_inset_top?: number | null;
  mandatory_gesture_inset_right?: number | null;
  mandatory_gesture_inset_bottom?: number | null;
  tappable_inset_left?: number | null;
  tappable_inset_right?: number | null;
  gesture_exclusion_rects_count?: number | null;
  insets_age_ms?: number | null;
  complete: boolean;
}

export interface KioskStateV2 {
  schema_version: 2;
  captured_at?: string;
  complete: boolean;
  // v1 compat (mirror v2 section values; present at the top level)
  uid?: number | null;
  is_platform_signed?: boolean | null;
  is_device_owner?: boolean | null;
  lock_task_mode_state?: number | null;
  back_gesture_inset_scale_right?: string | null;
  back_gesture_inset_scale_left?: string | null;
  navigation_mode?: string | null;
  one_handed_mode_enabled?: string | null;
  accessibility_display_magnification_enabled?: string | null;
  kiosk_state_complete?: boolean;
  // v2 sections
  identity_privilege: KioskV2IdentityPrivilege;
  lock_task_kiosk: KioskV2LockTaskKiosk;
  user_restrictions: KioskV2UserRestrictions;
  gesture_hardening: KioskV2GestureHardening;
  navigation: KioskV2Navigation;
  system_ui_hardening: KioskV2SystemUiHardening;
  lockscreen_keyguard: KioskV2LockscreenKeyguard;
  input_accessibility: KioskV2InputAccessibility;
  display_power: KioskV2DisplayPower;
  connectivity_policy: KioskV2ConnectivityPolicy;
  package_policy: KioskV2PackagePolicy;
  app_permissions: KioskV2AppPermissions;
  launcher_meta: KioskV2LauncherMeta;
  display_input_context: KioskV2DisplayInputContext;
  // Optional — present only on launcher builds that report runtime-drift telemetry.
  runtime_drift?: KioskV2RuntimeDrift;
}

export type KioskStateAny = KioskStateV1 | KioskStateV2;

// ─── schema detection ───────────────────────────────────────────────────

export type KioskSchema = 'none' | 'v1' | 'v2' | 'unknown';

export function detectKioskSchema(state: unknown): KioskSchema {
  if (state == null || typeof state !== 'object') return 'none';
  const sv = (state as { schema_version?: unknown }).schema_version;
  if (sv === 2) return 'v2';
  if (sv == null) return 'v1';
  return 'unknown';
}

// ─── label types & priority ─────────────────────────────────────────────

export type KioskHealthLabel =
  | 'no_snapshot'
  | 'snapshot_unknown_schema'
  | 'snapshot_incomplete'
  | 'not_platform_signed'
  | 'not_device_owner'
  | 'lock_task_inactive'
  | 'critical_app_missing'
  | 'back_gesture_drifted'
  | 'kiosk_drift_detected'
  | 'healthy';

export type KioskHealthTone = 'neutral' | 'warn' | 'fail' | 'ok';

export interface KioskHealthResult {
  /** Primary badge label, picked by priority order. */
  primary: KioskHealthLabel;
  /** All failed checks (excluding `healthy` / `no_snapshot`), in priority order. */
  issues: KioskHealthLabel[];
  /** True when every check above `healthy` passes. */
  isHealthy: boolean;
}

const PRIORITY_ORDER: KioskHealthLabel[] = [
  'no_snapshot',
  'snapshot_unknown_schema',
  'snapshot_incomplete',
  'not_platform_signed',
  'not_device_owner',
  'lock_task_inactive',
  'critical_app_missing',
  'back_gesture_drifted',
  'kiosk_drift_detected',
  'healthy',
];

const HEALTH_LABEL_TEXT: Record<KioskHealthLabel, string> = {
  no_snapshot: 'No kiosk snapshot',
  snapshot_unknown_schema: 'Unknown kiosk schema',
  snapshot_incomplete: 'Snapshot incomplete',
  not_platform_signed: 'Not platform-signed',
  not_device_owner: 'Not Device Owner',
  lock_task_inactive: 'Lock task inactive',
  critical_app_missing: 'Critical app missing',
  back_gesture_drifted: 'Back gesture drifted',
  kiosk_drift_detected: 'Kiosk drift detected',
  healthy: 'Healthy',
};

const HEALTH_LABEL_TONE: Record<KioskHealthLabel, KioskHealthTone> = {
  no_snapshot: 'neutral',
  snapshot_unknown_schema: 'warn',
  snapshot_incomplete: 'warn',
  not_platform_signed: 'fail',
  not_device_owner: 'fail',
  lock_task_inactive: 'warn',
  critical_app_missing: 'fail',
  back_gesture_drifted: 'warn',
  kiosk_drift_detected: 'warn',
  healthy: 'ok',
};

export function healthLabelText(label: KioskHealthLabel): string {
  return HEALTH_LABEL_TEXT[label];
}

export function healthLabelTone(label: KioskHealthLabel): KioskHealthTone {
  return HEALTH_LABEL_TONE[label];
}

// ─── v1 health computation (preserved) ──────────────────────────────────

/**
 * Compute the primary health label and any secondary issues from a v1 kiosk_state
 * snapshot.
 *
 * Rules (in priority order):
 *   1. no_snapshot           — kiosk_state missing entirely (pre-2.42.x launcher)
 *   2. snapshot_incomplete   — kiosk_state_complete === false
 *   3. not_platform_signed   — is_platform_signed === false OR (uid !== null && uid !== 1000)
 *   4. not_device_owner      — is_device_owner === false
 *   5. lock_task_inactive    — lock_task_mode_state === 0
 *   6. back_gesture_drifted  — either inset scale string is not "0"
 *   7. healthy               — all expected values match
 *
 * For v2 rows this is called via the schema dispatcher in `computeKioskHealth`.
 */
export function computeKioskHealthV1(state: KioskStateV1 | null | undefined): KioskHealthResult {
  if (state == null) {
    return { primary: 'no_snapshot', issues: [], isHealthy: false };
  }

  const issues: KioskHealthLabel[] = [];

  if (state.kiosk_state_complete === false) {
    issues.push('snapshot_incomplete');
  }
  if (state.is_platform_signed === false || (state.uid != null && state.uid !== 1000)) {
    issues.push('not_platform_signed');
  }
  if (state.is_device_owner === false) {
    issues.push('not_device_owner');
  }
  if (state.lock_task_mode_state === 0) {
    issues.push('lock_task_inactive');
  }
  if (
    (state.back_gesture_inset_scale_right != null && state.back_gesture_inset_scale_right !== '0') ||
    (state.back_gesture_inset_scale_left != null && state.back_gesture_inset_scale_left !== '0')
  ) {
    issues.push('back_gesture_drifted');
  }

  if (issues.length === 0) {
    return { primary: 'healthy', issues: [], isHealthy: true };
  }

  const primary = PRIORITY_ORDER.find((l) => issues.includes(l)) ?? 'healthy';
  return { primary, issues, isHealthy: false };
}

/**
 * Schema-aware dispatcher. Picks v1 or v2 rules based on `detectKioskSchema`.
 * Returns a `KioskHealthResult` shape compatible with the existing v1 callers.
 */
export function computeKioskHealth(
  state: KioskStateAny | null | undefined,
): KioskHealthResult {
  const schema = detectKioskSchema(state);
  if (schema === 'none') {
    return { primary: 'no_snapshot', issues: [], isHealthy: false };
  }
  if (schema === 'unknown') {
    // Fall back to v1 rules over any flat fields that may still be present,
    // but elevate the primary to snapshot_unknown_schema so SaaS hints clearly.
    const v1Result = computeKioskHealthV1(state as KioskStateV1);
    const issues: KioskHealthLabel[] = ['snapshot_unknown_schema', ...v1Result.issues];
    return { primary: 'snapshot_unknown_schema', issues, isHealthy: false };
  }
  if (schema === 'v2') {
    const r = computeKioskHealthV2(state as KioskStateV2);
    return { primary: r.primary, issues: r.issues, isHealthy: r.isHealthy };
  }
  return computeKioskHealthV1(state as KioskStateV1);
}

// ─── shared field display ───────────────────────────────────────────────

/** Display-ready description of one kiosk_state field for the UI grid. */
export interface KioskFieldDisplay {
  /** Operator-facing label. */
  label: string;
  /** Formatted value (e.g. "1 (LOCKED)", "Yes", "0", "unreadable"). */
  value: string;
  /** Visual tone for the value text. */
  tone: 'ok' | 'warn' | 'fail' | 'unreadable' | 'info';
  /** Expected value description (e.g. "1000", "Yes", "0", "1 (LOCKED)", "—"). */
  expected: string;
}

/**
 * Map each of the 10 v1 kiosk_state fields into a renderable card description.
 * Returns 10 entries in stable order; if `state` is null/undefined returns [].
 *
 * For v2 rows this still works because v2 mirrors the v1 fields at the top level.
 * v2-specific rendering uses `kioskV2SectionResults` instead.
 */
export function kioskFieldDisplays(state: KioskStateAny | null | undefined): KioskFieldDisplay[] {
  if (state == null) return [];
  const s = state as KioskStateV1;
  return [
    makeUidField(s.uid),
    makeBoolField('Platform-signed', s.is_platform_signed, true),
    makeBoolField('Device Owner', s.is_device_owner, true),
    makeLockTaskField(s.lock_task_mode_state),
    makeNavigationModeField(s.navigation_mode),
    makeBoolField('Snapshot complete', s.kiosk_state_complete, true),
    makeInsetField('Back gesture inset (right)', s.back_gesture_inset_scale_right),
    makeInsetField('Back gesture inset (left)', s.back_gesture_inset_scale_left),
    makeStringEqualsField('One-handed mode', s.one_handed_mode_enabled, '0'),
    makeStringEqualsField('Accessibility magnification', s.accessibility_display_magnification_enabled, '0'),
  ];
}

function makeUidField(uid: number | null | undefined): KioskFieldDisplay {
  if (uid === undefined) return { label: 'UID', value: 'missing', tone: 'unreadable', expected: '1000' };
  if (uid === null) return { label: 'UID', value: 'unreadable', tone: 'unreadable', expected: '1000' };
  return {
    label: 'UID',
    value: String(uid),
    tone: uid === 1000 ? 'ok' : 'fail',
    expected: '1000',
  };
}

function makeBoolField(
  label: string,
  value: boolean | null | undefined,
  expected: boolean,
): KioskFieldDisplay {
  const expectedText = expected ? 'Yes' : 'No';
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected: expectedText };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected: expectedText };
  return {
    label,
    value: value ? 'Yes' : 'No',
    tone: value === expected ? 'ok' : 'fail',
    expected: expectedText,
  };
}

function makeLockTaskField(value: number | null | undefined): KioskFieldDisplay {
  const label = 'Lock task state';
  const expected = '1 (LOCKED)';
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected };
  if (value === 0) return { label, value: '0 (INACTIVE)', tone: 'fail', expected };
  if (value === 1) return { label, value: '1 (LOCKED)', tone: 'ok', expected };
  if (value === 2) return { label, value: '2 (PINNED)', tone: 'ok', expected };
  return { label, value: String(value), tone: 'warn', expected };
}

function makeNavigationModeField(value: string | null | undefined): KioskFieldDisplay {
  const label = 'Navigation mode';
  const expected = '2 (gesture)';
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected };
  let text: string;
  if (value === '2') text = '2 (gesture)';
  else if (value === '1') text = '1 (two-button)';
  else if (value === '0') text = '0 (three-button)';
  else text = value;
  return { label, value: text, tone: value === '2' ? 'ok' : 'warn', expected };
}

function makeInsetField(label: string, value: string | null | undefined): KioskFieldDisplay {
  const expected = '0';
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected };
  return {
    label,
    value,
    tone: value === '0' ? 'ok' : 'warn',
    expected,
  };
}

function makeStringEqualsField(
  label: string,
  value: string | null | undefined,
  expected: string,
): KioskFieldDisplay {
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected };
  return {
    label,
    value,
    tone: value === expected ? 'ok' : 'warn',
    expected,
  };
}

// ─── v2 section model ───────────────────────────────────────────────────

export type KioskV2SectionId =
  | 'identity_privilege'
  | 'lock_task_kiosk'
  | 'user_restrictions'
  | 'gesture_hardening'
  | 'navigation'
  | 'system_ui_hardening'
  | 'lockscreen_keyguard'
  | 'input_accessibility'
  | 'display_power'
  | 'connectivity_policy'
  | 'package_policy'
  | 'app_permissions'
  | 'launcher_meta'
  | 'display_input_context';

export type KioskV2SectionStatus = 'ok' | 'info' | 'warn' | 'fail' | 'incomplete';

export interface KioskV2SectionResult {
  id: KioskV2SectionId;
  title: string;
  status: KioskV2SectionStatus;
  /** True iff the section's complete flag is false (read failure). */
  isIncomplete: boolean;
  /** True if this section should be in the "primary" always-visible row. */
  isPrimary: boolean;
  /** Display-ready field rows (or special structured rows for package/app sections). */
  fields: KioskFieldDisplay[];
  /** Number of fields with tone warn|fail. */
  issueCount: number;
  /** Number of fields that changed from a previous snapshot (only set during compare). */
  changedFields?: number;
  /** Per-field changed flags (label → changed bool). Used for diff highlight. */
  changedFieldLabels?: Set<string>;
  /** Special structured data for sections that need custom rendering. */
  special?:
    | { kind: 'app_permissions'; apps: AppPermissionRow[] }
    | { kind: 'package_policy'; packages: PackagePolicyRow[] };
}

export interface AppPermissionRow {
  appId: 'vortexnav' | 'truehelm' | 'vortextide' | 'vortexanchor';
  label: string;
  installed: boolean | null;
  /** Per-group requested/granted state. granted=null when not requested. */
  groups: Array<{
    id: 'location' | 'media' | 'bluetooth';
    label: string;
    requested: boolean | null;
    granted: boolean | null;
    tone: KioskFieldDisplay['tone'];
  }>;
  /** Worst-of-app severity (drives the row badge). */
  status: KioskV2SectionStatus;
}

export interface PackagePolicyRow {
  pkg: string;
  installed: boolean | null;
  hidden: boolean | null;
  expectedHidden: boolean | 'either';
  status: KioskV2SectionStatus;
  /** True if installed=null/false should NOT trigger any status — pkg may legitimately be absent. */
  optional: boolean;
}

export interface KioskHealthV2Result {
  schema: 'v2';
  primary: KioskHealthLabel;
  issues: KioskHealthLabel[];
  isHealthy: boolean;
  sections: KioskV2SectionResult[];
  /** Aggregate counts across all sections. */
  summary: { ok: number; info: number; warn: number; fail: number; incomplete: number };
}

const PRIMARY_SECTIONS: KioskV2SectionId[] = [
  'identity_privilege',
  'lock_task_kiosk',
  'gesture_hardening',
  'navigation',
  'user_restrictions',
  'package_policy',
  'app_permissions',
];

const ADVANCED_SECTIONS: KioskV2SectionId[] = [
  'system_ui_hardening',
  'lockscreen_keyguard',
  'input_accessibility',
  'display_power',
  'connectivity_policy',
  'launcher_meta',
  'display_input_context',
];

const SECTION_TITLE: Record<KioskV2SectionId, string> = {
  identity_privilege: 'Identity & privilege',
  lock_task_kiosk: 'Lock task & kiosk',
  user_restrictions: 'User restrictions',
  gesture_hardening: 'Gesture hardening',
  navigation: 'Navigation',
  system_ui_hardening: 'System UI hardening',
  lockscreen_keyguard: 'Lockscreen & keyguard',
  input_accessibility: 'Input & accessibility',
  display_power: 'Display & power',
  connectivity_policy: 'Connectivity policy',
  package_policy: 'Package policy',
  app_permissions: 'App permissions',
  launcher_meta: 'Launcher meta',
  display_input_context: 'Display & input context',
};

/** Worst-of severity reducer. Order: ok < info < warn < fail < incomplete. */
const SEVERITY_ORDER: KioskV2SectionStatus[] = ['ok', 'info', 'warn', 'fail', 'incomplete'];

function worstSeverity(a: KioskV2SectionStatus, b: KioskV2SectionStatus): KioskV2SectionStatus {
  return SEVERITY_ORDER.indexOf(a) >= SEVERITY_ORDER.indexOf(b) ? a : b;
}

function toneToSeverity(tone: KioskFieldDisplay['tone']): KioskV2SectionStatus {
  switch (tone) {
    case 'fail':
      return 'fail';
    case 'warn':
      return 'warn';
    case 'unreadable':
      return 'warn';
    case 'info':
      return 'info';
    case 'ok':
      return 'ok';
  }
}

function statusToTone(s: KioskV2SectionStatus): KioskFieldDisplay['tone'] {
  switch (s) {
    case 'ok':
      return 'ok';
    case 'info':
      return 'info';
    case 'warn':
      return 'warn';
    case 'fail':
      return 'fail';
    case 'incomplete':
      return 'unreadable';
  }
}

function reduceSectionStatus(fields: KioskFieldDisplay[]): KioskV2SectionStatus {
  let worst: KioskV2SectionStatus = 'ok';
  for (const f of fields) worst = worstSeverity(worst, toneToSeverity(f.tone));
  return worst;
}

// ─── v2 field builders (per-section field rules) ────────────────────────

function makeInfoField(label: string, value: unknown): KioskFieldDisplay {
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected: '—' };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected: '—' };
  return { label, value: String(value), tone: 'info', expected: '—' };
}

function makeStringSeverityField(
  label: string,
  value: string | null | undefined,
  expected: string,
  mismatchTone: 'warn' | 'fail' | 'info',
): KioskFieldDisplay {
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected };
  return {
    label,
    value,
    tone: value === expected ? 'ok' : mismatchTone,
    expected,
  };
}

function makeBoolSeverityField(
  label: string,
  value: boolean | null | undefined,
  expected: boolean,
  mismatchTone: 'warn' | 'fail' | 'info',
): KioskFieldDisplay {
  const expectedText = expected ? 'Yes' : 'No';
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected: expectedText };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected: expectedText };
  return {
    label,
    value: value ? 'Yes' : 'No',
    tone: value === expected ? 'ok' : mismatchTone,
    expected: expectedText,
  };
}

function makeNumberEqualsField(
  label: string,
  value: number | null | undefined,
  expected: number,
  mismatchTone: 'warn' | 'fail' | 'info',
): KioskFieldDisplay {
  const expectedText = String(expected);
  if (value === undefined) return { label, value: 'missing', tone: 'unreadable', expected: expectedText };
  if (value === null) return { label, value: 'unreadable', tone: 'unreadable', expected: expectedText };
  return {
    label,
    value: String(value),
    tone: value === expected ? 'ok' : mismatchTone,
    expected: expectedText,
  };
}

// ─── v2 per-section evaluators ──────────────────────────────────────────

function evalIdentityPrivilege(s: KioskV2IdentityPrivilege): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  // uid: fail if non-1000 and known
  fields.push(makeUidField(s.uid));
  // is_platform_signed: fail if false
  fields.push(makeBoolField('Platform-signed', s.is_platform_signed, true));
  // is_device_owner: fail if false
  fields.push(makeBoolField('Device Owner', s.is_device_owner, true));
  // android_sdk_int + device_model: informational
  fields.push(makeInfoField('Android SDK', s.android_sdk_int));
  fields.push(makeInfoField('Device model', s.device_model));
  return finalizeSection('identity_privilege', s.complete, fields);
}

function evalLockTaskKiosk(s: KioskV2LockTaskKiosk): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeLockTaskField(s.lock_task_mode_state));
  fields.push(makeNumberEqualsField('Lock task features', s.lock_task_features, 16, 'warn'));
  // packages_count: warn only if < 10 (sane fleet baseline)
  const ltpc = s.lock_task_packages_count;
  if (ltpc === undefined) {
    fields.push({ label: 'Lock task packages', value: 'missing', tone: 'unreadable', expected: '≥ 10' });
  } else if (ltpc === null) {
    fields.push({ label: 'Lock task packages', value: 'unreadable', tone: 'unreadable', expected: '≥ 10' });
  } else {
    fields.push({
      label: 'Lock task packages',
      value: String(ltpc),
      tone: ltpc >= 10 ? 'ok' : 'warn',
      expected: '≥ 10',
    });
  }
  fields.push(makeBoolSeverityField('VortexNav allowlisted', s.lock_task_includes_vortexnav, true, 'fail'));
  fields.push(makeBoolSeverityField('Settings allowlisted', s.lock_task_includes_settings, true, 'warn'));
  // keyguard_locked_runtime: informational (transient)
  fields.push(makeInfoField('Keyguard locked (runtime)', s.keyguard_locked_runtime));
  return finalizeSection('lock_task_kiosk', s.complete, fields);
}

function evalUserRestrictions(s: KioskV2UserRestrictions): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeBoolSeverityField('disallow_safe_boot', s.disallow_safe_boot, true, 'warn'));
  fields.push(makeBoolSeverityField('disallow_add_user', s.disallow_add_user, true, 'warn'));
  fields.push(makeBoolSeverityField('disallow_config_credentials', s.disallow_config_credentials, true, 'warn'));
  fields.push(makeBoolSeverityField('disallow_factory_reset', s.disallow_factory_reset, false, 'warn'));
  fields.push(makeBoolSeverityField('disallow_config_brightness', s.disallow_config_brightness, false, 'warn'));
  return finalizeSection('user_restrictions', s.complete, fields);
}

function evalGestureHardening(s: KioskV2GestureHardening): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  // All 17 keys: 16 expected "0", camera_double_tap_power_gesture_disabled expected "1"
  fields.push(makeStringSeverityField('back_gesture_inset_scale_right', s.back_gesture_inset_scale_right, '0', 'warn'));
  fields.push(makeStringSeverityField('back_gesture_inset_scale_left', s.back_gesture_inset_scale_left, '0', 'warn'));
  fields.push(makeStringSeverityField('camera_double_tap_power_gesture_disabled', s.camera_double_tap_power_gesture_disabled, '1', 'warn'));
  fields.push(makeStringSeverityField('assist_gesture_enabled', s.assist_gesture_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('assist_gesture_silence_alerts_enabled', s.assist_gesture_silence_alerts_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('assist_gesture_wake_enabled', s.assist_gesture_wake_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('one_handed_mode_enabled', s.one_handed_mode_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('one_handed_mode_activated', s.one_handed_mode_activated, '0', 'warn'));
  fields.push(makeStringSeverityField('notification_bubbles', s.notification_bubbles, '0', 'warn'));
  fields.push(makeStringSeverityField('accessibility_display_magnification_enabled', s.accessibility_display_magnification_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('accessibility_display_magnification_navbar_enabled', s.accessibility_display_magnification_navbar_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('wake_gesture_enabled', s.wake_gesture_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('pickup_gesture', s.pickup_gesture, '0', 'warn'));
  fields.push(makeStringSeverityField('tap_gesture', s.tap_gesture, '0', 'warn'));
  fields.push(makeStringSeverityField('doze_always_on', s.doze_always_on, '0', 'warn'));
  fields.push(makeStringSeverityField('doze_pulse_on_pick_up', s.doze_pulse_on_pick_up, '0', 'warn'));
  fields.push(makeStringSeverityField('doze_pulse_on_double_tap', s.doze_pulse_on_double_tap, '0', 'warn'));
  return finalizeSection('gesture_hardening', s.complete, fields);
}

function evalNavigation(s: KioskV2Navigation): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeNavigationModeField(s.navigation_mode));
  return finalizeSection('navigation', s.complete, fields);
}

function evalSystemUiHardening(s: KioskV2SystemUiHardening): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeStringSeverityField('policy_control', s.policy_control, 'immersive.full=*', 'warn'));
  fields.push(makeStringSeverityField('heads_up_notifications_enabled', s.heads_up_notifications_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('hide_error_dialogs', s.hide_error_dialogs, '1', 'warn'));
  fields.push(makeStringSeverityField('stay_on_while_plugged_in', s.stay_on_while_plugged_in, '7', 'warn'));
  fields.push(makeStringSeverityField('window_animation_scale', s.window_animation_scale, '0', 'warn'));
  fields.push(makeStringSeverityField('transition_animation_scale', s.transition_animation_scale, '0', 'warn'));
  fields.push(makeStringSeverityField('animator_duration_scale', s.animator_duration_scale, '0', 'warn'));
  fields.push(makeStringSeverityField('force_resizable_activities', s.force_resizable_activities, '1', 'warn'));
  return finalizeSection('system_ui_hardening', s.complete, fields);
}

function evalLockscreenKeyguard(s: KioskV2LockscreenKeyguard): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeStringSeverityField('lockscreen_disabled', s.lockscreen_disabled, '1', 'warn'));
  fields.push(makeStringSeverityField('lock_screen_show_notifications', s.lock_screen_show_notifications, '0', 'warn'));
  fields.push(makeStringSeverityField('lock_screen_allow_private_notifications', s.lock_screen_allow_private_notifications, '0', 'warn'));
  fields.push(makeStringSeverityField('lock_screen_lock_after_timeout', s.lock_screen_lock_after_timeout, '0', 'warn'));
  return finalizeSection('lockscreen_keyguard', s.complete, fields);
}

function evalInputAccessibility(s: KioskV2InputAccessibility): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  // default_input_method: ok if contains "helium314.keyboard"
  const dim = s.default_input_method;
  if (dim === undefined) {
    fields.push({ label: 'default_input_method', value: 'missing', tone: 'unreadable', expected: 'helium314.keyboard/.latin.LatinIME' });
  } else if (dim === null) {
    fields.push({ label: 'default_input_method', value: 'unreadable', tone: 'unreadable', expected: 'helium314.keyboard/.latin.LatinIME' });
  } else {
    fields.push({
      label: 'default_input_method',
      value: dim,
      tone: dim.includes('helium314.keyboard') ? 'ok' : 'warn',
      expected: 'helium314.keyboard/.latin.LatinIME',
    });
  }
  fields.push(makeBoolSeverityField('Heliboard in enabled IMEs', s.enabled_input_methods_includes_heliboard, true, 'warn'));
  fields.push(makeStringSeverityField('accessibility_enabled', s.accessibility_enabled, '1', 'warn'));
  fields.push(makeBoolSeverityField('BackKeyInterceptor enabled', s.back_key_interceptor_enabled, true, 'warn'));
  fields.push(makeStringSeverityField('immersive_mode_confirmations', s.immersive_mode_confirmations, 'confirmed', 'warn'));
  fields.push(makeStringSeverityField('install_non_market_apps', s.install_non_market_apps, '1', 'warn'));
  fields.push(makeStringSeverityField('swipe_bottom_to_notification_enabled', s.swipe_bottom_to_notification_enabled, '0', 'warn'));
  return finalizeSection('input_accessibility', s.complete, fields);
}

function evalDisplayPower(s: KioskV2DisplayPower): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeStringSeverityField('screen_off_timeout', s.screen_off_timeout, '2147483647', 'warn'));
  // screen_brightness_mode is info — launcher UI may legitimately change it
  if (s.screen_brightness_mode === undefined) {
    fields.push({ label: 'screen_brightness_mode', value: 'missing', tone: 'unreadable', expected: '—' });
  } else if (s.screen_brightness_mode === null) {
    fields.push({ label: 'screen_brightness_mode', value: 'unreadable', tone: 'unreadable', expected: '—' });
  } else {
    fields.push({ label: 'screen_brightness_mode', value: s.screen_brightness_mode, tone: 'info', expected: '—' });
  }
  fields.push(makeStringSeverityField('accelerometer_rotation', s.accelerometer_rotation, '0', 'warn'));
  fields.push(makeStringSeverityField('user_rotation', s.user_rotation, '1', 'warn'));
  fields.push(makeStringSeverityField('font_scale', s.font_scale, '0.8', 'warn'));
  fields.push(makeInfoField('status_bar_show_battery_percent', s.status_bar_show_battery_percent));
  fields.push(makeInfoField('time_12_24', s.time_12_24));
  fields.push(makeStringSeverityField('haptic_feedback_enabled', s.haptic_feedback_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('sound_effects_enabled', s.sound_effects_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('double_tap_to_wake', s.double_tap_to_wake, '0', 'warn'));
  fields.push(makeStringSeverityField('sleep_timeout', s.sleep_timeout, '-1', 'warn'));
  fields.push(makeNumberEqualsField('density_dpi', s.density_dpi, 240, 'warn'));
  return finalizeSection('display_power', s.complete, fields);
}

function evalConnectivityPolicy(s: KioskV2ConnectivityPolicy): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeStringSeverityField('data_roaming', s.data_roaming, '0', 'warn'));
  fields.push(makeStringSeverityField('mobile_data', s.mobile_data, '0', 'warn'));
  fields.push(makeStringSeverityField('wifi_scan_always_enabled', s.wifi_scan_always_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('ble_scan_always_enabled', s.ble_scan_always_enabled, '0', 'warn'));
  fields.push(makeStringSeverityField('adb_enabled', s.adb_enabled, '1', 'warn'));
  fields.push(makeStringSeverityField('development_settings_enabled', s.development_settings_enabled, '1', 'warn'));
  fields.push(makeStringSeverityField('airplane_mode_on', s.airplane_mode_on, '0', 'warn'));
  return finalizeSection('connectivity_policy', s.complete, fields);
}

/**
 * Per-package expected policy:
 *   - true            : pkg should be hidden if installed
 *   - false           : pkg should be unhidden if installed
 *   - 'either'        : pkg can be either; treated as info
 * `optional=true` packages are info when not installed (no warning).
 * `criticalMismatch=true` packages bump severity to fail on policy violation.
 */
interface PackagePolicySpec {
  pkg: string;
  expected: boolean | 'either';
  optional: boolean;
  criticalMismatch?: boolean;
}

const PACKAGE_POLICY_SPECS: PackagePolicySpec[] = [
  { pkg: 'com.android.launcher3', expected: true, optional: true, criticalMismatch: true },
  { pkg: 'com.android.vending', expected: true, optional: true },
  { pkg: 'com.google.android.gms', expected: false, optional: true, criticalMismatch: true },
  { pkg: 'com.google.android.gsf', expected: false, optional: true },
  { pkg: 'com.google.android.inputmethod.latin', expected: false, optional: true },
  { pkg: 'com.google.android.apps.photos', expected: true, optional: true },
  { pkg: 'com.google.android.apps.restore', expected: true, optional: true },
  { pkg: 'com.google.android.setupwizard', expected: true, optional: true },
  { pkg: 'com.google.android.gms.update', expected: true, optional: true },
];

function evalPackagePolicy(s: KioskV2PackagePolicy): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  const packages: PackagePolicyRow[] = [];
  for (const spec of PACKAGE_POLICY_SPECS) {
    const raw = s[spec.pkg];
    const entry: KioskV2PackageEntry =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as KioskV2PackageEntry)
        : {};
    const installed = entry.installed ?? null;
    const hidden = entry.hidden ?? null;
    let status: KioskV2SectionStatus;
    if (installed === false || installed === null) {
      status = spec.optional ? 'info' : 'warn';
    } else if (spec.expected === 'either') {
      status = 'info';
    } else if (hidden === spec.expected) {
      status = 'ok';
    } else if (hidden === null) {
      status = 'warn';
    } else {
      status = spec.criticalMismatch ? 'fail' : 'warn';
    }
    packages.push({
      pkg: spec.pkg,
      installed,
      hidden,
      expectedHidden: spec.expected,
      status,
      optional: spec.optional,
    });
    // Mirror as a field row for the changed-fields/diff machinery + simpler rendering paths.
    const valueText = installed === null
      ? 'unreadable'
      : installed
        ? `installed · hidden=${hidden == null ? '?' : hidden}`
        : 'not installed';
    const tone: KioskFieldDisplay['tone'] = statusToTone(status);
    const expected = spec.expected === 'either'
      ? '—'
      : spec.expected
        ? 'hidden if installed'
        : 'unhidden if installed';
    fields.push({ label: spec.pkg, value: valueText, tone, expected });
  }
  const result = finalizeSection('package_policy', s.complete, fields);
  result.special = { kind: 'package_policy', packages };
  return result;
}

interface AppSpec {
  appId: AppPermissionRow['appId'];
  pkg: string;
  label: string;
  /** If true, missing app installation is FAIL; else WARN. */
  critical: boolean;
}

const APP_SPECS: AppSpec[] = [
  { appId: 'vortexnav', pkg: 'com.vortexmarine.vortexnav', label: 'VortexNav', critical: true },
  { appId: 'truehelm', pkg: 'com.vortexmarine.truehelm', label: 'TrueHelm', critical: false },
  { appId: 'vortextide', pkg: 'com.vortexmarine.vortextide', label: 'VortexTide', critical: false },
  { appId: 'vortexanchor', pkg: 'com.vortexmarine.vortexanchor', label: 'VortexAnchor', critical: false },
];

function evalAppPermissions(s: KioskV2AppPermissions): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  const apps: AppPermissionRow[] = [];
  for (const spec of APP_SPECS) {
    const entry: KioskV2AppPermissionEntry | undefined = s[spec.appId];
    const installed = entry?.installed ?? null;
    const groups: AppPermissionRow['groups'] = [
      {
        id: 'location',
        label: 'Location',
        requested: entry?.location_requested ?? null,
        granted: entry?.location_granted ?? null,
        tone: 'info',
      },
      {
        id: 'media',
        label: 'Media',
        requested: entry?.media_requested ?? null,
        granted: entry?.media_granted ?? null,
        tone: 'info',
      },
      {
        id: 'bluetooth',
        label: 'Bluetooth',
        requested: entry?.bluetooth_requested ?? null,
        granted: entry?.bluetooth_granted ?? null,
        tone: 'info',
      },
    ];
    // Per-group tone semantics:
    //   - requested=false / null      → neutral (info) — not declared, nothing to grant
    //   - requested=true, granted=true → ok
    //   - requested=true, granted=false → FAIL for location, WARN for media/bluetooth
    //   - requested=true, granted=null  → unreadable (read failure)
    //   - per-app per-group can be promoted independently
    for (const g of groups) {
      if (g.requested === false || g.requested === null) {
        g.tone = 'info';
        continue;
      }
      if (g.granted === true) {
        g.tone = 'ok';
        continue;
      }
      if (g.granted === false) {
        g.tone = g.id === 'location' ? 'fail' : 'warn';
        continue;
      }
      g.tone = 'unreadable';
    }
    let appStatus: KioskV2SectionStatus;
    if (installed === false || installed === null) {
      appStatus = spec.critical ? 'fail' : 'warn';
    } else {
      // Roll up across groups, but skip 'info' (not-requested) groups so they
      // don't downgrade an otherwise-OK app. requested=false is observed-OK,
      // never a regression.
      appStatus = 'ok';
      for (const g of groups) {
        if (g.tone === 'info') continue;
        appStatus = worstSeverity(appStatus, toneToSeverity(g.tone));
      }
    }
    apps.push({ appId: spec.appId, label: spec.label, installed, groups, status: appStatus });
    // Mirror per-app summary as a field row.
    const valueText = installed === null
      ? 'unreadable'
      : installed
        ? `installed · ${groups.filter((g) => g.tone === 'ok').length}/${groups.filter((g) => g.requested === true).length} groups granted`
        : 'not installed';
    const fieldTone: KioskFieldDisplay['tone'] = statusToTone(appStatus);
    fields.push({
      label: spec.label,
      value: valueText,
      tone: fieldTone,
      expected: spec.critical ? 'installed + perms' : 'installed + perms (optional)',
    });
  }
  const result = finalizeSection('app_permissions', s.complete, fields);
  result.special = { kind: 'app_permissions', apps };
  return result;
}

function evalLauncherMeta(s: KioskV2LauncherMeta): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeInfoField('launcher_version_name', s.launcher_version_name));
  fields.push(makeInfoField('launcher_version_code', s.launcher_version_code));
  // signing_fingerprint_short: fail if known and != AOSP platform key
  const fp = s.signing_fingerprint_short;
  const expectedFp = 'c8a2e9bccf597c2f';
  if (fp === undefined) {
    fields.push({ label: 'signing_fingerprint_short', value: 'missing', tone: 'unreadable', expected: expectedFp });
  } else if (fp === null) {
    fields.push({ label: 'signing_fingerprint_short', value: 'unreadable', tone: 'unreadable', expected: expectedFp });
  } else {
    fields.push({
      label: 'signing_fingerprint_short',
      value: fp,
      tone: fp === expectedFp ? 'ok' : 'fail',
      expected: expectedFp,
    });
  }
  return finalizeSection('launcher_meta', s.complete, fields);
}

function evalDisplayInputContext(s: KioskV2DisplayInputContext): KioskV2SectionResult {
  const fields: KioskFieldDisplay[] = [];
  fields.push(makeInfoField('display_width_px', s.display_width_px));
  fields.push(makeInfoField('display_height_px', s.display_height_px));
  fields.push(makeInfoField('density_dpi', s.density_dpi));
  fields.push(makeInfoField('rotation', s.rotation));
  fields.push(makeInfoField('orientation', s.orientation));
  // pointer_location_enabled: warn if "1" (overlay on); ok if null/"0"
  const pl = s.pointer_location_enabled;
  if (pl === undefined) {
    fields.push({ label: 'pointer_location_enabled', value: 'missing', tone: 'unreadable', expected: '0 or unset' });
  } else if (pl === null) {
    fields.push({ label: 'pointer_location_enabled', value: 'unset', tone: 'ok', expected: '0 or unset' });
  } else if (pl === '0') {
    fields.push({ label: 'pointer_location_enabled', value: '0', tone: 'ok', expected: '0 or unset' });
  } else if (pl === '1') {
    fields.push({ label: 'pointer_location_enabled', value: '1', tone: 'warn', expected: '0 or unset' });
  } else {
    fields.push({ label: 'pointer_location_enabled', value: pl, tone: 'warn', expected: '0 or unset' });
  }
  return finalizeSection('display_input_context', s.complete, fields);
}

function finalizeSection(
  id: KioskV2SectionId,
  sectionComplete: boolean,
  fields: KioskFieldDisplay[],
): KioskV2SectionResult {
  let status: KioskV2SectionStatus = reduceSectionStatus(fields);
  const isIncomplete = sectionComplete === false;
  if (isIncomplete) status = worstSeverity(status, 'incomplete');
  const issueCount = fields.filter((f) => f.tone === 'warn' || f.tone === 'fail').length;
  return {
    id,
    title: SECTION_TITLE[id],
    status,
    isIncomplete,
    isPrimary: PRIMARY_SECTIONS.includes(id),
    fields,
    issueCount,
  };
}

// ─── v2 section results & overall health ────────────────────────────────

/**
 * Build the 14 v2 section results in operator-display order (primary first,
 * then advanced). Used by both `computeKioskHealthV2` and by the UI directly
 * when it needs to render sections without re-running label rules.
 */
export function kioskV2SectionResults(state: KioskStateV2): KioskV2SectionResult[] {
  const ordered: KioskV2SectionId[] = [...PRIMARY_SECTIONS, ...ADVANCED_SECTIONS];
  const evaluators: Record<KioskV2SectionId, () => KioskV2SectionResult> = {
    identity_privilege: () => evalIdentityPrivilege(state.identity_privilege),
    lock_task_kiosk: () => evalLockTaskKiosk(state.lock_task_kiosk),
    user_restrictions: () => evalUserRestrictions(state.user_restrictions),
    gesture_hardening: () => evalGestureHardening(state.gesture_hardening),
    navigation: () => evalNavigation(state.navigation),
    system_ui_hardening: () => evalSystemUiHardening(state.system_ui_hardening),
    lockscreen_keyguard: () => evalLockscreenKeyguard(state.lockscreen_keyguard),
    input_accessibility: () => evalInputAccessibility(state.input_accessibility),
    display_power: () => evalDisplayPower(state.display_power),
    connectivity_policy: () => evalConnectivityPolicy(state.connectivity_policy),
    package_policy: () => evalPackagePolicy(state.package_policy),
    app_permissions: () => evalAppPermissions(state.app_permissions),
    launcher_meta: () => evalLauncherMeta(state.launcher_meta),
    display_input_context: () => evalDisplayInputContext(state.display_input_context),
  };
  return ordered.map((id) => {
    try {
      return evaluators[id]();
    } catch {
      // Defensive: a malformed section object should never blow up the render.
      return {
        id,
        title: SECTION_TITLE[id],
        status: 'incomplete',
        isIncomplete: true,
        isPrimary: PRIMARY_SECTIONS.includes(id),
        fields: [],
        issueCount: 0,
      };
    }
  });
}

export function computeKioskHealthV2(state: KioskStateV2): KioskHealthV2Result {
  const sections = kioskV2SectionResults(state);
  const summary = { ok: 0, info: 0, warn: 0, fail: 0, incomplete: 0 };
  for (const s of sections) summary[s.status]++;

  const issues: KioskHealthLabel[] = [];

  // Read-completeness first: only when the read failed AND we don't already have
  // categorizable issues. `complete=false` ≠ unhealthy data — it's a read failure.
  if (state.complete === false) {
    issues.push('snapshot_incomplete');
  }

  // Identity-level failures (highest priority).
  const identity = state.identity_privilege;
  if (identity.is_platform_signed === false || (identity.uid != null && identity.uid !== 1000)) {
    issues.push('not_platform_signed');
  } else {
    // Also check signing fingerprint: a non-matching fingerprint with platform_signed=true
    // and uid=1000 is suspicious (compromised install).
    const fp = state.launcher_meta?.signing_fingerprint_short;
    if (fp != null && fp !== 'c8a2e9bccf597c2f') {
      issues.push('not_platform_signed');
    }
  }
  if (identity.is_device_owner === false) {
    issues.push('not_device_owner');
  }

  // Lock-task inactive.
  if (state.lock_task_kiosk?.lock_task_mode_state === 0) {
    issues.push('lock_task_inactive');
  }

  // Critical app missing: vortexnav not installed.
  const vortexnav = state.app_permissions?.vortexnav;
  if (vortexnav && vortexnav.installed === false) {
    issues.push('critical_app_missing');
  }

  // Back gesture drift — specific label for backward compatibility with v1 callers.
  const gh = state.gesture_hardening;
  if (
    (gh.back_gesture_inset_scale_right != null && gh.back_gesture_inset_scale_right !== '0') ||
    (gh.back_gesture_inset_scale_left != null && gh.back_gesture_inset_scale_left !== '0')
  ) {
    issues.push('back_gesture_drifted');
  }

  // Generic v2 drift: any non-OK section that isn't already covered by a higher
  // label. Coexists with `back_gesture_drifted` — drift can be both gesture-
  // specific AND wider. Primary picks the highest-priority label regardless.
  const anyDrift = sections.some((s) => s.status === 'warn' || s.status === 'fail');
  if (anyDrift) {
    issues.push('kiosk_drift_detected');
  }

  if (issues.length === 0) {
    return {
      schema: 'v2',
      primary: 'healthy',
      issues: [],
      isHealthy: true,
      sections,
      summary,
    };
  }

  const primary = PRIORITY_ORDER.find((l) => issues.includes(l)) ?? 'healthy';
  return {
    schema: 'v2',
    primary,
    issues,
    isHealthy: false,
    sections,
    summary,
  };
}

// ─── comparison (v1↔v1, v2↔v2, cross-schema) ────────────────────────────

export interface KioskChangedField {
  /** Section id when v2; null when comparing v1 fields. */
  section: KioskV2SectionId | null;
  /** Field label (operator-facing). */
  label: string;
  /** Stringified previous value. */
  previousValue: string;
  /** Stringified current value. */
  currentValue: string;
}

export interface KioskCompareResult {
  /** Same schema for both rows? false → cross-schema (still safe to compare v1 fields only). */
  schemaMatch: boolean;
  /** Set to true if there is something to compare. False when previous is null. */
  comparable: boolean;
  /** Total changed fields across all of the diff. */
  totalChanged: number;
  /** Per-section changed counts (v2-vs-v2 only). */
  sectionChangedCounts?: Record<KioskV2SectionId, number>;
  /** Set of section ids that had at least one change (v2-vs-v2 only). */
  changedSections?: Set<KioskV2SectionId>;
  /** Detailed per-field diffs. */
  changes: KioskChangedField[];
}

const V1_COMPAT_FIELDS: Array<keyof KioskStateV1> = [
  'uid',
  'is_platform_signed',
  'is_device_owner',
  'lock_task_mode_state',
  'back_gesture_inset_scale_right',
  'back_gesture_inset_scale_left',
  'navigation_mode',
  'one_handed_mode_enabled',
  'accessibility_display_magnification_enabled',
];

function v1FieldLabel(key: string): string {
  switch (key) {
    case 'uid':
      return 'UID';
    case 'is_platform_signed':
      return 'Platform-signed';
    case 'is_device_owner':
      return 'Device Owner';
    case 'lock_task_mode_state':
      return 'Lock task state';
    case 'back_gesture_inset_scale_right':
      return 'Back gesture inset (right)';
    case 'back_gesture_inset_scale_left':
      return 'Back gesture inset (left)';
    case 'navigation_mode':
      return 'Navigation mode';
    case 'one_handed_mode_enabled':
      return 'One-handed mode';
    case 'accessibility_display_magnification_enabled':
      return 'Accessibility magnification';
    default:
      return key;
  }
}

function stringifyForDiff(v: unknown): string {
  if (v === undefined) return 'missing';
  if (v === null) return 'unreadable';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

/**
 * Compare two kiosk_state snapshots.
 *
 *   - v1 ↔ v1: compare the 9 shared v1 fields (excluding kiosk_state_complete).
 *   - v2 ↔ v2: compare every leaf field across every section + the v1 flat compat
 *              fields. Returns per-section counts and a changedSections set.
 *   - v1 ↔ v2 or v2 ↔ v1: compare only the v1-compatible flat fields (since the
 *              previous side doesn't have v2 sections to diff). schemaMatch=false.
 *   - either side `unknown`: best-effort v1-shared comparison; schemaMatch=false.
 */
export function compareKiosk(
  latest: KioskStateAny | null | undefined,
  previous: KioskStateAny | null | undefined,
): KioskCompareResult {
  if (latest == null || previous == null) {
    return { schemaMatch: false, comparable: false, totalChanged: 0, changes: [] };
  }

  const latestSchema = detectKioskSchema(latest);
  const previousSchema = detectKioskSchema(previous);
  const schemaMatch = latestSchema === previousSchema && latestSchema !== 'unknown' && latestSchema !== 'none';

  if (latestSchema === 'v2' && previousSchema === 'v2') {
    return compareV2V2(latest as KioskStateV2, previous as KioskStateV2);
  }

  // v1↔v1 or cross-schema: compare only the v1 flat fields.
  return compareV1Shared(latest as KioskStateV1, previous as KioskStateV1, schemaMatch);
}

function compareV1Shared(
  latest: KioskStateV1,
  previous: KioskStateV1,
  schemaMatch: boolean,
): KioskCompareResult {
  const changes: KioskChangedField[] = [];
  for (const key of V1_COMPAT_FIELDS) {
    const a = (latest as Record<string, unknown>)[key];
    const b = (previous as Record<string, unknown>)[key];
    if (stringifyForDiff(a) !== stringifyForDiff(b)) {
      changes.push({
        section: null,
        label: v1FieldLabel(String(key)),
        previousValue: stringifyForDiff(b),
        currentValue: stringifyForDiff(a),
      });
    }
  }
  return {
    schemaMatch,
    comparable: true,
    totalChanged: changes.length,
    changes,
  };
}

const V2_LEAF_RULES: Record<KioskV2SectionId, Array<{ key: string; label: string }>> = {
  identity_privilege: [
    { key: 'uid', label: 'UID' },
    { key: 'is_platform_signed', label: 'Platform-signed' },
    { key: 'is_device_owner', label: 'Device Owner' },
    { key: 'android_sdk_int', label: 'Android SDK' },
    { key: 'device_model', label: 'Device model' },
  ],
  lock_task_kiosk: [
    { key: 'lock_task_mode_state', label: 'Lock task state' },
    { key: 'lock_task_features', label: 'Lock task features' },
    { key: 'lock_task_packages_count', label: 'Lock task packages' },
    { key: 'lock_task_includes_vortexnav', label: 'VortexNav allowlisted' },
    { key: 'lock_task_includes_settings', label: 'Settings allowlisted' },
    { key: 'keyguard_locked_runtime', label: 'Keyguard locked (runtime)' },
  ],
  user_restrictions: [
    { key: 'disallow_safe_boot', label: 'disallow_safe_boot' },
    { key: 'disallow_add_user', label: 'disallow_add_user' },
    { key: 'disallow_config_credentials', label: 'disallow_config_credentials' },
    { key: 'disallow_factory_reset', label: 'disallow_factory_reset' },
    { key: 'disallow_config_brightness', label: 'disallow_config_brightness' },
  ],
  gesture_hardening: [
    { key: 'back_gesture_inset_scale_right', label: 'back_gesture_inset_scale_right' },
    { key: 'back_gesture_inset_scale_left', label: 'back_gesture_inset_scale_left' },
    { key: 'camera_double_tap_power_gesture_disabled', label: 'camera_double_tap_power_gesture_disabled' },
    { key: 'assist_gesture_enabled', label: 'assist_gesture_enabled' },
    { key: 'assist_gesture_silence_alerts_enabled', label: 'assist_gesture_silence_alerts_enabled' },
    { key: 'assist_gesture_wake_enabled', label: 'assist_gesture_wake_enabled' },
    { key: 'one_handed_mode_enabled', label: 'one_handed_mode_enabled' },
    { key: 'one_handed_mode_activated', label: 'one_handed_mode_activated' },
    { key: 'notification_bubbles', label: 'notification_bubbles' },
    { key: 'accessibility_display_magnification_enabled', label: 'accessibility_display_magnification_enabled' },
    { key: 'accessibility_display_magnification_navbar_enabled', label: 'accessibility_display_magnification_navbar_enabled' },
    { key: 'wake_gesture_enabled', label: 'wake_gesture_enabled' },
    { key: 'pickup_gesture', label: 'pickup_gesture' },
    { key: 'tap_gesture', label: 'tap_gesture' },
    { key: 'doze_always_on', label: 'doze_always_on' },
    { key: 'doze_pulse_on_pick_up', label: 'doze_pulse_on_pick_up' },
    { key: 'doze_pulse_on_double_tap', label: 'doze_pulse_on_double_tap' },
  ],
  navigation: [{ key: 'navigation_mode', label: 'Navigation mode' }],
  system_ui_hardening: [
    { key: 'policy_control', label: 'policy_control' },
    { key: 'heads_up_notifications_enabled', label: 'heads_up_notifications_enabled' },
    { key: 'hide_error_dialogs', label: 'hide_error_dialogs' },
    { key: 'stay_on_while_plugged_in', label: 'stay_on_while_plugged_in' },
    { key: 'window_animation_scale', label: 'window_animation_scale' },
    { key: 'transition_animation_scale', label: 'transition_animation_scale' },
    { key: 'animator_duration_scale', label: 'animator_duration_scale' },
    { key: 'force_resizable_activities', label: 'force_resizable_activities' },
  ],
  lockscreen_keyguard: [
    { key: 'lockscreen_disabled', label: 'lockscreen_disabled' },
    { key: 'lock_screen_show_notifications', label: 'lock_screen_show_notifications' },
    { key: 'lock_screen_allow_private_notifications', label: 'lock_screen_allow_private_notifications' },
    { key: 'lock_screen_lock_after_timeout', label: 'lock_screen_lock_after_timeout' },
  ],
  input_accessibility: [
    { key: 'default_input_method', label: 'default_input_method' },
    { key: 'enabled_input_methods_includes_heliboard', label: 'Heliboard in enabled IMEs' },
    { key: 'accessibility_enabled', label: 'accessibility_enabled' },
    { key: 'back_key_interceptor_enabled', label: 'BackKeyInterceptor enabled' },
    { key: 'immersive_mode_confirmations', label: 'immersive_mode_confirmations' },
    { key: 'install_non_market_apps', label: 'install_non_market_apps' },
    { key: 'swipe_bottom_to_notification_enabled', label: 'swipe_bottom_to_notification_enabled' },
  ],
  display_power: [
    { key: 'screen_off_timeout', label: 'screen_off_timeout' },
    { key: 'screen_brightness_mode', label: 'screen_brightness_mode' },
    { key: 'accelerometer_rotation', label: 'accelerometer_rotation' },
    { key: 'user_rotation', label: 'user_rotation' },
    { key: 'font_scale', label: 'font_scale' },
    { key: 'status_bar_show_battery_percent', label: 'status_bar_show_battery_percent' },
    { key: 'time_12_24', label: 'time_12_24' },
    { key: 'haptic_feedback_enabled', label: 'haptic_feedback_enabled' },
    { key: 'sound_effects_enabled', label: 'sound_effects_enabled' },
    { key: 'double_tap_to_wake', label: 'double_tap_to_wake' },
    { key: 'sleep_timeout', label: 'sleep_timeout' },
    { key: 'density_dpi', label: 'density_dpi' },
  ],
  connectivity_policy: [
    { key: 'data_roaming', label: 'data_roaming' },
    { key: 'mobile_data', label: 'mobile_data' },
    { key: 'wifi_scan_always_enabled', label: 'wifi_scan_always_enabled' },
    { key: 'ble_scan_always_enabled', label: 'ble_scan_always_enabled' },
    { key: 'adb_enabled', label: 'adb_enabled' },
    { key: 'development_settings_enabled', label: 'development_settings_enabled' },
    { key: 'airplane_mode_on', label: 'airplane_mode_on' },
  ],
  // For package_policy and app_permissions we diff per-package / per-app entries below.
  package_policy: [],
  app_permissions: [],
  launcher_meta: [
    { key: 'launcher_version_name', label: 'launcher_version_name' },
    { key: 'launcher_version_code', label: 'launcher_version_code' },
    { key: 'signing_fingerprint_short', label: 'signing_fingerprint_short' },
  ],
  display_input_context: [
    { key: 'display_width_px', label: 'display_width_px' },
    { key: 'display_height_px', label: 'display_height_px' },
    { key: 'density_dpi', label: 'density_dpi' },
    { key: 'rotation', label: 'rotation' },
    { key: 'orientation', label: 'orientation' },
    { key: 'pointer_location_enabled', label: 'pointer_location_enabled' },
  ],
};

function compareV2V2(latest: KioskStateV2, previous: KioskStateV2): KioskCompareResult {
  const changes: KioskChangedField[] = [];
  const sectionChangedCounts: Record<string, number> = {};
  const changedSections = new Set<KioskV2SectionId>();

  for (const sectionId of Object.keys(V2_LEAF_RULES) as KioskV2SectionId[]) {
    sectionChangedCounts[sectionId] = 0;
    const rules = V2_LEAF_RULES[sectionId];
    const a = (latest as unknown as Record<string, Record<string, unknown>>)[sectionId];
    const b = (previous as unknown as Record<string, Record<string, unknown>>)[sectionId];
    if (rules.length > 0) {
      for (const rule of rules) {
        const av = a?.[rule.key];
        const bv = b?.[rule.key];
        if (stringifyForDiff(av) !== stringifyForDiff(bv)) {
          changes.push({
            section: sectionId,
            label: rule.label,
            previousValue: stringifyForDiff(bv),
            currentValue: stringifyForDiff(av),
          });
          sectionChangedCounts[sectionId]++;
          changedSections.add(sectionId);
        }
      }
      continue;
    }
    // Dynamic-shape sections (package_policy, app_permissions): compare known keys/apps.
    if (sectionId === 'package_policy') {
      for (const spec of PACKAGE_POLICY_SPECS) {
        const aPkg = (a as Record<string, KioskV2PackageEntry | undefined>)?.[spec.pkg];
        const bPkg = (b as Record<string, KioskV2PackageEntry | undefined>)?.[spec.pkg];
        for (const k of ['installed', 'hidden'] as const) {
          const av = aPkg?.[k];
          const bv = bPkg?.[k];
          if (stringifyForDiff(av) !== stringifyForDiff(bv)) {
            changes.push({
              section: sectionId,
              label: `${spec.pkg}.${k}`,
              previousValue: stringifyForDiff(bv),
              currentValue: stringifyForDiff(av),
            });
            sectionChangedCounts[sectionId]++;
            changedSections.add(sectionId);
          }
        }
      }
      continue;
    }
    if (sectionId === 'app_permissions') {
      for (const spec of APP_SPECS) {
        const aApp = (a as Record<string, KioskV2AppPermissionEntry | undefined>)?.[spec.appId];
        const bApp = (b as Record<string, KioskV2AppPermissionEntry | undefined>)?.[spec.appId];
        const keys: Array<keyof KioskV2AppPermissionEntry> = [
          'installed',
          'location_requested',
          'location_granted',
          'media_requested',
          'media_granted',
          'bluetooth_requested',
          'bluetooth_granted',
        ];
        for (const k of keys) {
          const av = aApp?.[k];
          const bv = bApp?.[k];
          if (stringifyForDiff(av) !== stringifyForDiff(bv)) {
            changes.push({
              section: sectionId,
              label: `${spec.appId}.${k}`,
              previousValue: stringifyForDiff(bv),
              currentValue: stringifyForDiff(av),
            });
            sectionChangedCounts[sectionId]++;
            changedSections.add(sectionId);
          }
        }
      }
      continue;
    }
  }

  return {
    schemaMatch: true,
    comparable: true,
    totalChanged: changes.length,
    sectionChangedCounts: sectionChangedCounts as Record<KioskV2SectionId, number>,
    changedSections,
    changes,
  };
}

// ─── runtime_drift: live SystemUI gesture / geometry verdict ─────────────
//
// The runtime_drift section is cross-cutting: it compares the LIVE runtime
// signals (window-gesture insets + display geometry) against the STORED
// hardening (gesture_hardening.back_gesture_inset_scale_*). The verdict is
// computed here (server side) rather than on-device so the heuristic can be
// tuned without shipping a launcher OTA. Pure — no UI, no network.

export interface RuntimeDriftField {
  /** Operator-facing signal label. */
  label: string;
  /** Stored / expected value as a string ("0", "consistent", "—"). */
  stored: string;
  /** Live runtime value as a string ("48px", "portrait", "not captured"). */
  runtime: string;
  /** Visual tone for the runtime value. */
  tone: 'ok' | 'warn' | 'fail' | 'unreadable' | 'info';
  /** Short explanation shown when the row is drifted. */
  note?: string;
}

export interface RuntimeDriftResult {
  /** True iff the snapshot included a runtime_drift block at all. */
  present: boolean;
  /** True iff live window insets were captured (Activity supplied them). */
  insetsCaptured: boolean;
  status: KioskV2SectionStatus;
  /** The smoking gun: right-edge back gesture live at runtime despite stored "0". */
  gestureDesyncSuspected: boolean;
  /** rotation / bounds / orientation disagree (geometry desync surfaced in-window). */
  geometryInconsistent: boolean;
  /** One-line operator summary. */
  summary: string;
  fields: RuntimeDriftField[];
}

function isHardenedZero(stored: string | null): boolean {
  return stored === '0' || stored === '0.0';
}

/** Stored back-gesture inset scale from the v2 section or the v1/top-level mirror. */
function storedBackGestureScale(
  state: KioskStateAny,
  side: 'left' | 'right',
): string | null {
  const key =
    side === 'right' ? 'back_gesture_inset_scale_right' : 'back_gesture_inset_scale_left';
  const gh = (state as KioskStateV2).gesture_hardening;
  if (gh && typeof gh === 'object') {
    const v = (gh as unknown as Record<string, unknown>)[key];
    if (v != null) return String(v);
  }
  const top = (state as unknown as Record<string, unknown>)[key];
  if (top != null) return String(top);
  return null;
}

/**
 * Compute the runtime-drift verdict from a kiosk_state snapshot.
 *
 * Returns `present: false` for snapshots with no runtime_drift block (older
 * launcher builds) so the UI can hide the card entirely rather than show noise.
 */
export function computeRuntimeDrift(
  state: KioskStateAny | null | undefined,
): RuntimeDriftResult {
  const empty: RuntimeDriftResult = {
    present: false,
    insetsCaptured: false,
    status: 'incomplete',
    gestureDesyncSuspected: false,
    geometryInconsistent: false,
    summary: 'No runtime snapshot in this report.',
    fields: [],
  };
  if (state == null || typeof state !== 'object') return empty;
  const rd = (state as KioskStateV2).runtime_drift;
  if (rd == null || typeof rd !== 'object') return empty;

  const fields: RuntimeDriftField[] = [];
  const insetsCaptured = rd.insets_captured === true;

  // ── Back gesture inset (right) — the primary desync signal ──────────────
  const storedRight = storedBackGestureScale(state, 'right');
  const rightInset = rd.system_gesture_inset_right;
  let gestureDesyncSuspected = false;
  {
    let tone: RuntimeDriftField['tone'];
    let runtime: string;
    let note: string | undefined;
    if (!insetsCaptured || rightInset == null) {
      tone = 'unreadable';
      runtime = 'not captured';
    } else if (rightInset > 0 && isHardenedZero(storedRight)) {
      tone = 'fail';
      runtime = `${rightInset}px`;
      note = 'right-edge back gesture is live at runtime despite hardened settings';
      gestureDesyncSuspected = true;
    } else if (rightInset > 0) {
      tone = 'warn';
      runtime = `${rightInset}px`;
      note = 'right gesture inset is non-zero';
    } else {
      tone = 'ok';
      runtime = `${rightInset}px`;
    }
    fields.push({
      label: 'Back gesture inset (right)',
      stored: storedRight ?? '—',
      runtime,
      tone,
      note,
    });
  }

  // ── Back gesture inset (left) — secondary ───────────────────────────────
  {
    const storedLeft = storedBackGestureScale(state, 'left');
    const leftInset = rd.system_gesture_inset_left;
    let tone: RuntimeDriftField['tone'];
    let runtime: string;
    let note: string | undefined;
    if (!insetsCaptured || leftInset == null) {
      tone = 'unreadable';
      runtime = 'not captured';
    } else if (leftInset > 0 && isHardenedZero(storedLeft)) {
      tone = 'warn';
      runtime = `${leftInset}px`;
      note = 'left gesture inset non-zero despite hardened settings';
    } else if (leftInset > 0) {
      tone = 'warn';
      runtime = `${leftInset}px`;
    } else {
      tone = 'ok';
      runtime = `${leftInset}px`;
    }
    fields.push({
      label: 'Back gesture inset (left)',
      stored: storedLeft ?? '—',
      runtime,
      tone,
      note,
    });
  }

  // ── Display geometry consistency ────────────────────────────────────────
  const geometryInconsistent = rd.landscape_consistent === false;
  {
    const orient = rd.config_orientation ?? '—';
    const rotStr = rd.display_rotation != null ? `rot ${rd.display_rotation}` : 'rot —';
    const bw = rd.max_window_bounds_w;
    const bh = rd.max_window_bounds_h;
    const boundsStr = bw != null && bh != null ? `${bw}×${bh}` : '—';
    const detail = `${orient}, ${rotStr}, ${boundsStr}`;
    let tone: RuntimeDriftField['tone'];
    let runtime: string;
    let note: string | undefined;
    if (rd.landscape_consistent == null) {
      tone = 'unreadable';
      runtime = 'unreadable';
    } else if (rd.landscape_consistent) {
      tone = 'ok';
      runtime = `yes (${detail})`;
    } else {
      tone = 'warn';
      runtime = `no (${detail})`;
      note = 'display rotation, bounds, and orientation disagree (SystemUI geometry desync)';
    }
    fields.push({ label: 'Geometry consistent', stored: 'consistent', runtime, tone, note });
  }

  // ── Informational insets (status-bar pull region + exclusion strips) ────
  if (insetsCaptured) {
    const mt = rd.mandatory_gesture_inset_top;
    fields.push({
      label: 'Status-bar gesture inset (top)',
      stored: '—',
      runtime: mt != null ? `${mt}px` : 'not captured',
      tone: 'info',
    });
    const ex = rd.gesture_exclusion_rects_count;
    fields.push({
      label: 'Gesture exclusion strips',
      stored: '—',
      runtime: ex != null ? `${ex} rect${ex === 1 ? '' : 's'}` : 'not captured',
      tone: 'info',
    });
  }

  // ── Status + summary ────────────────────────────────────────────────────
  // Severity is driven by the worst field tone (so any anomalous inset surfaces)
  // combined with read-completeness. fail > warn > incomplete > ok.
  const hasFail = fields.some((f) => f.tone === 'fail');
  const hasWarn = fields.some((f) => f.tone === 'warn');
  const hasUnreadable = fields.some((f) => f.tone === 'unreadable');

  let status: KioskV2SectionStatus;
  let summary: string;
  if (hasFail) {
    status = 'fail';
    summary = gestureDesyncSuspected
      ? 'Right-edge back gesture appears live at runtime despite hardened settings — the recurring SystemUI gesture desync. A reboot clears it.'
      : 'Runtime gesture state has drifted from the stored hardening.';
  } else if (hasWarn) {
    status = 'warn';
    summary = geometryInconsistent
      ? 'Display rotation, bounds, and orientation disagree at runtime — possible SystemUI geometry desync.'
      : 'Runtime gesture insets differ from the hardened baseline.';
  } else if (!insetsCaptured || rd.complete === false || hasUnreadable) {
    status = 'incomplete';
    summary = !insetsCaptured
      ? 'Live window insets were not captured this check; only geometry signals are shown.'
      : 'Runtime snapshot was only partially read.';
  } else {
    status = 'ok';
    summary = 'Live gesture insets and display geometry match the hardened settings.';
  }

  return {
    present: true,
    insetsCaptured,
    status,
    gestureDesyncSuspected,
    geometryInconsistent,
    summary,
    fields,
  };
}
